import { BaseScene } from './BaseScene';
import { t } from '../i18n/lang';
import { AudioManager, BackgroundMusic } from '../systems/AudioManager';
import { AdManager, AdType } from '../systems/AdManager';
import { DeveloperOptionsManager } from '../utils/DeveloperOptions';

export class MainMenuScene extends BaseScene {
  protected override audioManager: AudioManager;
  private adManager: AdManager;
  private devManager: DeveloperOptionsManager;

  constructor() {
    super({ key: 'MainMenu' });
  }

  override create(): void {
    super.create();
    
    // Initialize managers
    this.audioManager = AudioManager.getInstance(this);
    this.adManager = AdManager.getInstance();
    this.devManager = DeveloperOptionsManager.getInstance();
    
    // Setup secret key detector for developer mode
    DeveloperOptionsManager.setupSecretKeyDetector(this);
    
    // Play main menu music
    this.audioManager.playMusic(BackgroundMusic.MAIN_MENU);
    
    this.addModernBackground();
    this.createHeaderModern();
    this.createProfileCardModern();
    this.createStartButtonModern();
    this.createFooterMenuBarModern();
    this.createCornerRewardButtons();
    
    // Create developer panel if enabled
    if (this.devManager.isDeveloperModeEnabled()) {
      this.devManager.createDevPanel(this);
    }
  }

  private checkDailyLogin(): void {
    const isFirstLoginToday = this.adManager.checkDailyLogin();
    
    if (isFirstLoginToday) {
      // Show daily login notification
      this.showDailyLoginNotification();
    }
  }

  private showDailyLoginNotification(): void {
    const dailyInfo = this.adManager.getDailyInfo();
    
    // Create notification popup
    const centerX = this.cameras.main.centerX;
    const centerY = this.cameras.main.centerY;
    
    // Background overlay
    const overlay = this.add.rectangle(0, 0, this.cameras.main.width, this.cameras.main.height, 0x000000, 0.7)
      .setOrigin(0)
      .setInteractive();
    
    // Notification panel
    const panel = this.add.rectangle(centerX, centerY, 400, 300, 0x1a1a1a, 0.95)
      .setStrokeStyle(3, 0xFFD700);
    
    // Title
    this.add.text(centerX, centerY - 100, '🎉 DAILY LOGIN!', {
      fontSize: '28px',
      color: '#FFD700',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    
    // Streak info
    this.add.text(centerX, centerY - 60, `Day ${dailyInfo.currentStreak} Streak!`, {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    
    // Reward preview
    const nextReward = dailyInfo.nextReward;
    this.add.text(centerX, centerY - 20, nextReward.description, {
      fontSize: '16px',
      color: '#4CAF50',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    
    this.add.text(centerX, centerY + 10, `💰 ${nextReward.gold} Gold`, {
      fontSize: '18px',
      color: '#FFD700',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    
    if (nextReward.ruby > 0) {
      this.add.text(centerX, centerY + 35, `💎 ${nextReward.ruby} Ruby`, {
        fontSize: '18px',
        color: '#E91E63',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }
    
    // Close button
    const closeBtn = this.add.text(centerX, centerY + 80, 'CONTINUE', {
      fontSize: '20px',
      color: '#ffffff',
      backgroundColor: '#4CAF50',
      padding: { x: 20, y: 10 },
      fontFamily: 'Arial',
    })
    .setOrigin(0.5)
    .setInteractive({ useHandCursor: true });
    
    closeBtn.on('pointerdown', () => {
      this.audioManager.playButtonClick();
      overlay.destroy();
      panel.destroy();
      closeBtn.destroy();
      this.children.list.filter(child => 
        child instanceof Phaser.GameObjects.Text && 
        child.text.includes('DAILY LOGIN')
      ).forEach(child => child.destroy());
    });
    
    closeBtn.on('pointerover', () => {
      this.audioManager.playButtonHover();
      closeBtn.setStyle({ backgroundColor: '#388E3C' });
    });
    
    closeBtn.on('pointerout', () => {
      closeBtn.setStyle({ backgroundColor: '#4CAF50' });
    });
  }

  private createProfileSummary(): void {
    const userProfile = (window as any).userProfile;
    const width = this.cameras.main.width;
    // Panel background with shadow and border
    const bg = this.add.graphics();
    bg.fillStyle(0x222b3a, 0.92);
    bg.fillRoundedRect(30, 30, width - 60, 100, 24);
    bg.lineStyle(4, 0x74b9ff, 0.7);
    bg.strokeRoundedRect(30, 30, width - 60, 100, 24);
    bg.setDepth(100);
    // Shadow
    const shadow = this.add.graphics();
    shadow.fillStyle(0x000000, 0.18);
    shadow.fillRoundedRect(36, 44, width - 72, 100, 28);
    shadow.setDepth(99);
    // Large profile icon
    this.add.text(60, 80, '👤', {
      fontSize: '48px', fontFamily: 'Arial', color: '#FFD700'
    }).setOrigin(0.5).setDepth(101);
    // Level
    this.add.text(120, 65, `LV ${userProfile.profileLevel}`, {
      fontSize: '26px', fontFamily: 'Arial Black', color: '#FFD700', fontStyle: 'bold', stroke: '#2c3e50', strokeThickness: 2
    }).setDepth(101);
    // Earth
    this.add.text(220, 65, `🌍 ${userProfile.equippedEarth?.toUpperCase() || ''}`, {
      fontSize: '22px', fontFamily: 'Arial Black', color: '#74b9ff', stroke: '#222b3a', strokeThickness: 2
    }).setDepth(101);
    // Towers
    this.add.text(400, 65, `🏗️ ${userProfile.equippedTowers?.join(', ').toUpperCase() || ''}`, {
      fontSize: '18px', fontFamily: 'Arial', color: '#f39c12', fontStyle: 'bold'
    }).setDepth(101);
    // Pets
    this.add.text(650, 65, `🐾 ${userProfile.equippedPets?.join(', ').toUpperCase() || ''}`, {
      fontSize: '18px', fontFamily: 'Arial', color: '#9b59b6', fontStyle: 'bold'
    }).setDepth(101);
  }

  private createProfileSummaryModern(): void {
    const userProfile = (window as any).userProfile;
    const centerX = this.cameras.main.centerX;
    // Panel background
    const bg = this.add.graphics();
    bg.fillStyle(0x222b3a, 0.95);
    bg.fillRoundedRect(centerX - 320, 160, 640, 110, 32);
    bg.lineStyle(4, 0x74b9ff, 0.7);
    bg.strokeRoundedRect(centerX - 320, 160, 640, 110, 32);
    bg.setDepth(100);
    // Shadow
    const shadow = this.add.graphics();
    shadow.fillStyle(0x000000, 0.13);
    shadow.fillRoundedRect(centerX - 316, 172, 632, 110, 36);
    shadow.setDepth(99);
    // Icon lớn
    this.add.text(centerX - 260, 215, '👤', { fontSize: '60px', fontFamily: 'Arial', color: '#FFD700' }).setOrigin(0.5).setDepth(101);
    // Level
    this.add.text(centerX - 180, 195, `LV ${userProfile.profileLevel}`, { fontSize: '28px', fontFamily: 'Arial Black', color: '#FFD700', fontStyle: 'bold', stroke: '#2c3e50', strokeThickness: 2 }).setDepth(101);
    // Earth
    this.add.text(centerX - 60, 195, `🌍 ${userProfile.equippedEarth?.toUpperCase() || ''}`, { fontSize: '24px', fontFamily: 'Arial Black', color: '#74b9ff', stroke: '#222b3a', strokeThickness: 2 }).setDepth(101);
    // Towers
    this.add.text(centerX + 100, 195, `🏗️ ${userProfile.equippedTowers?.join(', ').toUpperCase() || ''}`, { fontSize: '20px', fontFamily: 'Arial', color: '#f39c12', fontStyle: 'bold' }).setDepth(101);
    // Pets
    this.add.text(centerX + 260, 195, `🐾 ${userProfile.equippedPets?.join(', ').toUpperCase() || ''}`, { fontSize: '20px', fontFamily: 'Arial', color: '#9b59b6', fontStyle: 'bold' }).setDepth(101);
  }

  private createFooterMenu(): void {
    const centerX = this.cameras.main.centerX;
    const footerY = this.cameras.main.height - 60;
    const buttons = [
      { text: t('start'), key: 'EarthSelection', color: 0x27ae60, icon: '🚀' },
      { text: 'INVENTORY', key: 'Inventory', color: 0x3498db, icon: '🎒' },
      { text: 'SHOP', key: 'Shop', color: 0xf39c12, icon: '🛒' },
      { text: t('achievements'), key: 'Achievement', color: 0xf39c12, icon: '🏆' },
      { text: t('leaderboard'), key: 'Leaderboard', color: 0x9b59b6, icon: '📊' },
      { text: t('settings'), key: 'Settings', color: 0x95a5a6, icon: '⚙️' }
    ];
    const btnWidth = 140;
    const spacing = 24;
    const totalWidth = buttons.length * btnWidth + (buttons.length - 1) * spacing;
    let startX = centerX - totalWidth / 2 + btnWidth / 2;
    // Footer background
    const footerBg = this.add.graphics();
    footerBg.fillStyle(0x1a1a2e, 0.96);
    footerBg.fillRoundedRect(startX - btnWidth / 2 - 32, footerY - 38, totalWidth + 64, 76, 36);
    footerBg.setDepth(50);
    footerBg.lineStyle(3, 0x74b9ff, 0.5);
    footerBg.strokeRoundedRect(startX - btnWidth / 2 - 32, footerY - 38, totalWidth + 64, 76, 36);
    // Shadow
    const footerShadow = this.add.graphics();
    footerShadow.fillStyle(0x000000, 0.18);
    footerShadow.fillRoundedRect(startX - btnWidth / 2 - 28, footerY - 30, totalWidth + 56, 76, 40);
    footerShadow.setDepth(49);
    // Buttons
    buttons.forEach((button, idx) => {
      this.createMenuButton(
        button.text,
        footerY,
        button.color,
        button.icon,
        button.key,
        undefined,
        startX + idx * (btnWidth + spacing)
      );
    });
  }

  private createFooterMenuModern(): void {
    const centerX = this.cameras.main.centerX;
    const footerY = this.cameras.main.height - 80;
    const buttons = [
      { text: t('start'), key: 'EarthSelection', color: 0x27ae60, icon: '🚀' },
      { text: 'INVENTORY', key: 'Inventory', color: 0x3498db, icon: '🎒' },
      { text: 'SHOP', key: 'Shop', color: 0xf39c12, icon: '🛒' },
      { text: t('achievements'), key: 'Achievement', color: 0xf39c12, icon: '🏆' },
      { text: t('leaderboard'), key: 'Leaderboard', color: 0x9b59b6, icon: '📊' },
      { text: t('settings'), key: 'Settings', color: 0x95a5a6, icon: '⚙️' }
    ];
    const btnWidth = 150;
    const spacing = 32;
    const totalWidth = buttons.length * btnWidth + (buttons.length - 1) * spacing;
    let startX = centerX - totalWidth / 2 + btnWidth / 2;
    // Footer background
    const footerBg = this.add.graphics();
    footerBg.fillStyle(0x1a1a2e, 0.98);
    footerBg.fillRoundedRect(startX - btnWidth / 2 - 36, footerY - 44, totalWidth + 72, 88, 40);
    footerBg.setDepth(50);
    footerBg.lineStyle(3, 0x74b9ff, 0.5);
    footerBg.strokeRoundedRect(startX - btnWidth / 2 - 36, footerY - 44, totalWidth + 72, 88, 40);
    // Shadow
    const footerShadow = this.add.graphics();
    footerShadow.fillStyle(0x000000, 0.13);
    footerShadow.fillRoundedRect(startX - btnWidth / 2 - 32, footerY - 36, totalWidth + 64, 88, 44);
    footerShadow.setDepth(49);
    // Buttons
    buttons.forEach((button, idx) => {
      this.createMenuButtonModern(
        button.text,
        footerY,
        button.color,
        button.icon,
        button.key,
        undefined,
        startX + idx * (btnWidth + spacing)
      );
    });
  }

  private createMenuButton(text: string, y: number, color: number, icon: string, sceneKey: string, container?: Phaser.GameObjects.Container, xOverride?: number): void {
    const x = xOverride !== undefined ? xOverride : 0;
    const buttonContainer = this.add.container(x, y);
    // Button background with gradient, shadow, border
    const buttonBg = this.add.graphics();
    buttonBg.fillGradientStyle(color, color, color * 0.8, color * 0.8, 1);
    buttonBg.fillRoundedRect(-60, -28, 120, 56, 24);
    buttonBg.lineStyle(3, 0xffffff, 0.8);
    buttonBg.strokeRoundedRect(-60, -28, 120, 56, 24);
    buttonContainer.add(buttonBg);
    // Shadow
    const shadow = this.add.graphics();
    shadow.fillStyle(0x000000, 0.22);
    shadow.fillRoundedRect(-58, -24, 116, 52, 22);
    buttonContainer.add(shadow);
    // Icon lớn
    const iconText = this.add.text(-28, 0, icon, {
      fontSize: '32px', fontFamily: 'Arial'
    }).setOrigin(0.5);
    buttonContainer.add(iconText);
    // Button text
    const buttonText = this.add.text(22, 0, text, {
      fontSize: '18px', fontFamily: 'Arial Black', color: '#ffffff', fontStyle: 'bold'
    }).setOrigin(0.5);
    buttonContainer.add(buttonText);
    // Hover glow effect
    const hoverGlow = this.add.graphics();
    hoverGlow.fillStyle(color, 0.18);
    hoverGlow.fillRoundedRect(-65, -33, 130, 66, 28);
    hoverGlow.setVisible(false);
    buttonContainer.add(hoverGlow);
    // Interactive
    buttonContainer.setInteractive(
      new Phaser.Geom.Rectangle(-60, -28, 120, 56),
      Phaser.Geom.Rectangle.Contains
    );
    buttonContainer.on('pointerover', () => {
      hoverGlow.setVisible(true);
      this.tweens.add({
        targets: buttonContainer,
        scaleX: 1.08,
        scaleY: 1.08,
        duration: 180,
        ease: 'Power2'
      });
    });
    buttonContainer.on('pointerout', () => {
      hoverGlow.setVisible(false);
      this.tweens.add({
        targets: buttonContainer,
        scaleX: 1,
        scaleY: 1,
        duration: 180,
        ease: 'Power2'
      });
    });
    buttonContainer.on('pointerdown', () => {
      this.tweens.add({
        targets: buttonContainer,
        scaleX: 0.95,
        scaleY: 0.95,
        duration: 100,
        yoyo: true,
        ease: 'Power2'
      });
      this.time.delayedCall(200, () => {
        this.scene.start(sceneKey);
      });
    });
    if (container) container.add(buttonContainer);
    // Entrance animation
    buttonContainer.setAlpha(0);
    buttonContainer.setScale(0.8);
    this.time.delayedCall(100 * (container ? container.list.length : 0), () => {
      this.tweens.add({
        targets: buttonContainer,
        alpha: 1,
        scaleX: 1,
        scaleY: 1,
        duration: 400,
        ease: 'Back.easeOut'
      });
    });
  }

  private createMenuButtonModern(text: string, y: number, color: number, icon: string, sceneKey: string, container?: Phaser.GameObjects.Container, xOverride?: number): void {
    const x = xOverride !== undefined ? xOverride : 0;
    const buttonContainer = this.add.container(x, y);
    // Button background
    const buttonBg = this.add.graphics();
    buttonBg.fillGradientStyle(color, color, color * 0.8, color * 0.8, 1);
    buttonBg.fillRoundedRect(-70, -32, 140, 64, 28);
    buttonBg.lineStyle(3, 0xffffff, 0.8);
    buttonBg.strokeRoundedRect(-70, -32, 140, 64, 28);
    buttonContainer.add(buttonBg);
    // Shadow
    const shadow = this.add.graphics();
    shadow.fillStyle(0x000000, 0.18);
    shadow.fillRoundedRect(-68, -28, 136, 60, 24);
    buttonContainer.add(shadow);
    // Icon lớn
    const iconText = this.add.text(-32, 0, icon, { fontSize: '36px', fontFamily: 'Arial' }).setOrigin(0.5);
    buttonContainer.add(iconText);
    // Button text
    const buttonText = this.add.text(28, 0, text, { fontSize: '20px', fontFamily: 'Arial Black', color: '#fff', fontStyle: 'bold' }).setOrigin(0.5);
    buttonContainer.add(buttonText);
    // Hover glow effect
    const hoverGlow = this.add.graphics();
    hoverGlow.fillStyle(color, 0.22);
    hoverGlow.fillRoundedRect(-75, -37, 150, 74, 32);
    hoverGlow.setVisible(false);
    buttonContainer.add(hoverGlow);
    // Interactive
    buttonContainer.setInteractive(
      new Phaser.Geom.Rectangle(-70, -32, 140, 64),
      Phaser.Geom.Rectangle.Contains
    );
    buttonContainer.on('pointerover', () => {
      hoverGlow.setVisible(true);
      this.tweens.add({ targets: buttonContainer, scaleX: 1.10, scaleY: 1.10, duration: 180, ease: 'Power2' });
    });
    buttonContainer.on('pointerout', () => {
      hoverGlow.setVisible(false);
      this.tweens.add({ targets: buttonContainer, scaleX: 1, scaleY: 1, duration: 180, ease: 'Power2' });
    });
    buttonContainer.on('pointerdown', () => {
      this.tweens.add({ targets: buttonContainer, scaleX: 0.95, scaleY: 0.95, duration: 100, yoyo: true, ease: 'Power2' });
      this.time.delayedCall(200, () => { this.scene.start(sceneKey); });
    });
    if (container) container.add(buttonContainer);
    // Entrance animation
    buttonContainer.setAlpha(0);
    buttonContainer.setScale(0.8);
    this.time.delayedCall(100 * (container ? container.list.length : 0), () => {
      this.tweens.add({ targets: buttonContainer, alpha: 1, scaleX: 1, scaleY: 1, duration: 400, ease: 'Back.easeOut' });
    });
  }

  private createDailyRewardsUI(): void {
    const dailyInfo = this.adManager.getDailyInfo();
    
    // Daily rewards panel (top-left)
    const panelX = 20;
    const panelY = 80;
    
    // Background
    this.add.rectangle(panelX + 100, panelY + 60, 200, 120, 0x1a1a1a, 0.8)
      .setOrigin(0.5)
      .setStrokeStyle(2, 0x4CAF50);
    
    // Title
    this.add.text(panelX + 100, panelY + 20, '📅 DAILY REWARDS', {
      fontSize: '14px',
      color: '#4CAF50',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    
    // Streak display
    this.add.text(panelX + 100, panelY + 40, `🔥 ${dailyInfo.currentStreak}-Day Streak`, {
      fontSize: '12px',
      color: '#FFD700',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    
    // Next reward
    const nextReward = dailyInfo.nextReward;
    this.add.text(panelX + 100, panelY + 60, `Next: ${nextReward.gold}💰`, {
      fontSize: '11px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    
    // Claim button
    if (dailyInfo.canClaimDaily) {
      const claimBtn = this.add.text(panelX + 100, panelY + 85, 'CLAIM!', {
        fontSize: '12px',
        color: '#ffffff',
        backgroundColor: '#4CAF50',
        padding: { x: 8, y: 4 },
        fontFamily: 'Arial',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
      
      claimBtn.on('pointerdown', () => {
        this.claimDailyReward();
      });
      
      claimBtn.on('pointerover', () => {
        this.audioManager.playButtonHover();
        claimBtn.setStyle({ backgroundColor: '#388E3C' });
      });
      
      claimBtn.on('pointerout', () => {
        claimBtn.setStyle({ backgroundColor: '#4CAF50' });
      });
    } else {
      this.add.text(panelX + 100, panelY + 85, 'Claimed ✓', {
        fontSize: '11px',
        color: '#888888',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }
  }

  private createAdRewardsSection(): void {
    const panelX = this.cameras.main.width - 220;
    const panelY = 80;
    
    // Background
    this.add.rectangle(panelX + 100, panelY + 80, 200, 160, 0x1a1a1a, 0.8)
      .setOrigin(0.5)
      .setStrokeStyle(2, 0xFF9800);
    
    // Title
    this.add.text(panelX + 100, panelY + 20, '📺 FREE REWARDS', {
      fontSize: '14px',
      color: '#FF9800',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    
    // Ad session info
    const adInfo = this.adManager.getAdSessionInfo();
    this.add.text(panelX + 100, panelY + 40, `Ads: ${adInfo.adsWatched}/${adInfo.maxAds}`, {
      fontSize: '11px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    
    // Available ads
    const availableAds = this.adManager.getAvailableAds('menu');
    let yOffset = 60;
    
    const adButtons = [
      { type: AdType.FREE_GOLD, text: '💰 Free Gold', color: '#FFD700' },
      { type: AdType.FREE_RUBY, text: '💎 Free Ruby', color: '#E91E63' },
      { type: AdType.FREE_EQUIPMENT, text: '⚔️ Free Item', color: '#9C27B0' },
    ];
    
    adButtons.forEach((ad, index) => {
      const canWatch = availableAds.includes(ad.type);
      const buttonColor = canWatch ? ad.color : '#666666';
      
      const adBtn = this.add.text(panelX + 100, panelY + yOffset, ad.text, {
        fontSize: '10px',
        color: '#ffffff',
        backgroundColor: buttonColor,
        padding: { x: 6, y: 3 },
        fontFamily: 'Arial',
      })
      .setOrigin(0.5);
      
      if (canWatch) {
        adBtn.setInteractive({ useHandCursor: true });
        
        adBtn.on('pointerdown', () => {
          this.watchAdForReward(ad.type);
        });
        
        adBtn.on('pointerover', () => {
          this.audioManager.playButtonHover();
          adBtn.setAlpha(0.8);
        });
        
        adBtn.on('pointerout', () => {
          adBtn.setAlpha(1);
        });
      }
      
      yOffset += 25;
    });
    
    // Cooldown display
    if (!adInfo.canWatch && adInfo.cooldownRemaining > 0) {
      this.add.text(panelX + 100, panelY + yOffset + 10, `Cooldown: ${this.adManager.getCooldownString()}`, {
        fontSize: '10px',
        color: '#ff6666',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }
  }

  private async claimDailyReward(): Promise<void> {
    const reward = this.adManager.claimDailyReward();
    if (!reward) return;
    
    this.audioManager.playButtonClick();
    
    // Apply reward to user profile
    const userProfile = (window as any).userProfile;
    userProfile.gold += reward.gold;
    userProfile.ruby += reward.ruby;
    
    if (reward.equipment) {
      if (!userProfile.ownedItems) userProfile.ownedItems = [];
      userProfile.ownedItems.push({
        id: reward.equipment,
        level: 1,
        obtainedAt: Date.now(),
      });
    }
    
    (window as any).userProfile = userProfile;
    
    // Show reward notification
    this.showRewardNotification(`Daily Reward Claimed!\n+${reward.gold} Gold` + 
      (reward.ruby > 0 ? `\n+${reward.ruby} Ruby` : '') +
      (reward.equipment ? `\n+${reward.equipment}` : ''));
    
    // Refresh the scene to update UI
    this.scene.restart();
  }

  private async watchAdForReward(adType: AdType): Promise<void> {
    this.audioManager.playButtonClick();
    
    // Show loading screen
    this.showAdLoadingScreen();
    
    try {
      const reward = await this.adManager.watchAd(adType);
      
      if (reward) {
        // Apply reward
        const userProfile = (window as any).userProfile;
        AdManager.applyAdReward(reward, userProfile);
        (window as any).userProfile = userProfile;
        
        // Show reward notification
        this.showRewardNotification(`${reward.icon} ${reward.description}\n+${reward.amount || 0}`);
        
        // Refresh UI
        this.scene.restart();
      }
    } catch (error) {
      console.error('Ad watch failed:', error);
      this.showRewardNotification('❌ Ad failed to load. Please try again later.');
    }
  }

  private showAdLoadingScreen(): void {
    // Simple loading overlay
    const overlay = this.add.rectangle(0, 0, this.cameras.main.width, this.cameras.main.height, 0x000000, 0.8)
      .setOrigin(0);
    
    const loadingText = this.add.text(this.cameras.main.centerX, this.cameras.main.centerY, 'Loading Ad...', {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    
    // Remove after 3 seconds (simulated ad time)
    this.time.delayedCall(3000, () => {
      overlay.destroy();
      loadingText.destroy();
    });
  }

  private showRewardNotification(message: string): void {
    const centerX = this.cameras.main.centerX;
    const centerY = this.cameras.main.centerY;
    
    const notification = this.add.text(centerX, centerY - 100, message, {
      fontSize: '18px',
      color: '#4CAF50',
      backgroundColor: 'rgba(0,0,0,0.8)',
      padding: { x: 20, y: 15 },
      fontFamily: 'Arial',
      align: 'center',
    }).setOrigin(0.5);
    
    // Animate and remove
    this.tweens.add({
      targets: notification,
      alpha: 0,
      y: centerY - 150,
      duration: 2000,
      onComplete: () => {
        notification.destroy();
      }
    });
  }

  protected override addBackground(): void {
    // Create deep space gradient background
    const background = this.add.graphics();
    background.fillGradientStyle(0x0c0c1a, 0x1a1a2e, 0x16213e, 0x0f0f23, 1);
    background.fillRect(0, 0, this.cameras.main.width, this.cameras.main.height);
    
    // Create animated starfield
    this.createStarfield();
    
    // Create floating particles
    this.createFloatingParticles();
    
    // Create Earth in background
    this.createBackgroundEarth();
  }

  private addModernBackground(): void {
    // Gradient + starfield
    const g = this.add.graphics();
    g.fillGradientStyle(0x0c0c1a, 0x1a1a2e, 0x16213e, 0x0f0f23, 1);
    g.fillRect(0, 0, this.cameras.main.width, this.cameras.main.height);
    // Starfield
    for (let i = 0; i < 80; i++) {
      const x = Phaser.Math.Between(0, this.cameras.main.width);
      const y = Phaser.Math.Between(0, this.cameras.main.height);
      const star = this.add.circle(x, y, Phaser.Math.Between(1, 2), 0xffffff, Phaser.Math.FloatBetween(0.2, 0.7));
      this.tweens.add({ targets: star, alpha: 0.1, duration: Phaser.Math.Between(1000, 3000), yoyo: true, repeat: -1 });
    }
  }

  private createHeader(): void {
    const centerX = this.cameras.main.centerX;
    // Game logo/title
    this.add.text(centerX, 70, '🌌 EARTH DEFENSE', {
      fontSize: '54px', fontFamily: 'Arial Black', color: '#74b9ff', fontStyle: 'bold', stroke: '#222b3a', strokeThickness: 6
    }).setOrigin(0.5);
    // Subtitle
    this.add.text(centerX, 120, 'Defend the Last Planet', {
      fontSize: '20px', fontFamily: 'Arial', color: '#bdc3c7', fontStyle: 'italic', stroke: '#0c0c1a', strokeThickness: 2
    }).setOrigin(0.5);
  }

  private createHeaderModern(): void {
    const centerX = this.cameras.main.centerX;
    this.add.text(centerX, 70, '🌌 EARTH DEFENSE', {
      fontSize: '60px', fontFamily: 'Arial Black', color: '#74b9ff', fontStyle: 'bold', stroke: '#222b3a', strokeThickness: 8
    }).setOrigin(0.5);
    this.add.text(centerX, 130, 'Defend the Last Planet', {
      fontSize: '22px', fontFamily: 'Arial', color: '#bdc3c7', fontStyle: 'italic', stroke: '#0c0c1a', strokeThickness: 2
    }).setOrigin(0.5);
  }

  private createProfileCardModern(): void {
    const userProfile = (window as any).userProfile;
    const centerX = this.cameras.main.centerX;
    const y = 200;
    const card = this.add.graphics();
    card.fillStyle(0x222b3a, 0.97);
    card.fillRoundedRect(centerX - 180, y, 360, 90, 28);
    card.lineStyle(3, 0x74b9ff, 0.7);
    card.strokeRoundedRect(centerX - 180, y, 360, 90, 28);
    card.setDepth(100);
    // Avatar
    this.add.text(centerX - 120, y + 45, '👤', { fontSize: '48px', fontFamily: 'Arial', color: '#FFD700' }).setOrigin(0.5).setDepth(101);
    // Name & Level
    this.add.text(centerX - 60, y + 30, `LV ${userProfile.profileLevel}`, { fontSize: '22px', fontFamily: 'Arial Black', color: '#FFD700', fontStyle: 'bold' }).setDepth(101);
    this.add.text(centerX - 60, y + 60, userProfile.name || 'Player', { fontSize: '18px', fontFamily: 'Arial', color: '#fff' }).setDepth(101);
    // Gold
    this.add.text(centerX + 40, y + 35, `💰 ${userProfile.gold}`, { fontSize: '18px', fontFamily: 'Arial', color: '#FFD700' }).setDepth(101);
    // Ruby
    this.add.text(centerX + 120, y + 35, `💎 ${userProfile.ruby}`, { fontSize: '18px', fontFamily: 'Arial', color: '#E91E63' }).setDepth(101);
  }

  private createStartButtonModern(): void {
    const centerX = this.cameras.main.centerX;
    const y = 330;
    const btn = this.add.text(centerX, y, '🚀 START', {
      fontSize: '38px', fontFamily: 'Arial Black', color: '#fff', backgroundColor: '#27ae60', padding: { x: 40, y: 18 }, fontStyle: 'bold', stroke: '#145a32', strokeThickness: 4
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    btn.on('pointerdown', () => {
      this.audioManager.playButtonClick();
      this.scene.start('EarthSelection');
    });
    btn.on('pointerover', () => btn.setStyle({ backgroundColor: '#219150' }));
    btn.on('pointerout', () => btn.setStyle({ backgroundColor: '#27ae60' }));
    // Entrance animation
    btn.setAlpha(0);
    btn.setScale(0.8);
    this.tweens.add({ targets: btn, alpha: 1, scaleX: 1, scaleY: 1, duration: 500, ease: 'Back.easeOut' });
  }

  private createFooterMenuBarModern(): void {
    const centerX = this.cameras.main.centerX;
    const footerY = this.cameras.main.height - 60;
    const buttons = [
      { text: '🎒', label: 'Inventory', key: 'Inventory' },
      { text: '🛒', label: 'Shop', key: 'Shop' },
      { text: '🏆', label: 'Achievements', key: 'Achievement' },
      { text: '📊', label: 'Leaderboard', key: 'Leaderboard' },
      { text: '⚙️', label: 'Settings', key: 'Settings' }
    ];
    const btnWidth = 90;
    const spacing = 24;
    const totalWidth = buttons.length * btnWidth + (buttons.length - 1) * spacing;
    let startX = centerX - totalWidth / 2 + btnWidth / 2;
    buttons.forEach((button, idx) => {
      const x = startX + idx * (btnWidth + spacing);
      const btn = this.add.text(x, footerY, button.text, {
        fontSize: '36px', fontFamily: 'Arial', color: '#fff', backgroundColor: '#1a1a2e', padding: { x: 18, y: 10 }, stroke: '#74b9ff', strokeThickness: 2
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => {
        this.audioManager.playButtonClick();
        this.scene.start(button.key);
      });
      btn.on('pointerover', () => btn.setStyle({ backgroundColor: '#222b3a' }));
      btn.on('pointerout', () => btn.setStyle({ backgroundColor: '#1a1a2e' }));
      // Label
      this.add.text(x, footerY + 32, button.label, { fontSize: '13px', fontFamily: 'Arial', color: '#bdc3c7' }).setOrigin(0.5);
    });
  }

  private createCornerRewardButtons(): void {
    // Daily Reward (top left)
    const dailyBtn = this.add.text(38, 38, '📅', {
      fontSize: '32px', fontFamily: 'Arial', color: '#4CAF50', backgroundColor: '#1a1a1a', padding: { x: 10, y: 8 }, stroke: '#4CAF50', strokeThickness: 2
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    dailyBtn.on('pointerdown', () => this.showDailyLoginNotification());
    dailyBtn.on('pointerover', () => dailyBtn.setStyle({ backgroundColor: '#222b3a' }));
    dailyBtn.on('pointerout', () => dailyBtn.setStyle({ backgroundColor: '#1a1a1a' }));
    // Ad Reward (top right)
    const adBtn = this.add.text(this.cameras.main.width - 38, 38, '🎁', {
      fontSize: '32px', fontFamily: 'Arial', color: '#FF9800', backgroundColor: '#1a1a1a', padding: { x: 10, y: 8 }, stroke: '#FF9800', strokeThickness: 2
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    adBtn.on('pointerdown', () => this.showAdRewardsPopup());
    adBtn.on('pointerover', () => adBtn.setStyle({ backgroundColor: '#222b3a' }));
    adBtn.on('pointerout', () => adBtn.setStyle({ backgroundColor: '#1a1a1a' }));
  }

  private showAdRewardsPopup(): void {
    // Show a popup with ad rewards info and buttons (reuse/adapt createAdRewardsSection logic)
    const centerX = this.cameras.main.centerX;
    const centerY = this.cameras.main.centerY;
    const overlay = this.add.rectangle(0, 0, this.cameras.main.width, this.cameras.main.height, 0x000000, 0.7).setOrigin(0).setInteractive();
    const panel = this.add.rectangle(centerX, centerY, 340, 260, 0x1a1a1a, 0.95).setStrokeStyle(3, 0xFF9800);
    this.add.text(centerX, centerY - 90, '🎁 FREE REWARDS', { fontSize: '22px', color: '#FF9800', fontFamily: 'Arial' }).setOrigin(0.5);
    const adInfo = this.adManager.getAdSessionInfo();
    this.add.text(centerX, centerY - 60, `Ads: ${adInfo.adsWatched}/${adInfo.maxAds}`, { fontSize: '14px', color: '#fff', fontFamily: 'Arial' }).setOrigin(0.5);
    const availableAds = this.adManager.getAvailableAds('menu');
    const adButtons = [
      { type: AdType.FREE_GOLD, text: '💰 Free Gold', color: '#FFD700' },
      { type: AdType.FREE_RUBY, text: '💎 Free Ruby', color: '#E91E63' },
      { type: AdType.FREE_EQUIPMENT, text: '⚔️ Free Item', color: '#9C27B0' },
    ];
    let yOffset = -30;
    adButtons.forEach((ad, index) => {
      const canWatch = availableAds.includes(ad.type);
      const buttonColor = canWatch ? ad.color : '#666666';
      const adBtn = this.add.text(centerX, centerY + yOffset, ad.text, {
        fontSize: '16px', color: '#fff', backgroundColor: buttonColor, padding: { x: 12, y: 6 }, fontFamily: 'Arial',
      }).setOrigin(0.5);
      if (canWatch) {
        adBtn.setInteractive({ useHandCursor: true });
        adBtn.on('pointerdown', () => { this.watchAdForReward(ad.type); overlay.destroy(); panel.destroy(); adBtn.destroy(); });
        adBtn.on('pointerover', () => adBtn.setAlpha(0.8));
        adBtn.on('pointerout', () => adBtn.setAlpha(1));
      }
      yOffset += 38;
    });
    if (!adInfo.canWatch && adInfo.cooldownRemaining > 0) {
      this.add.text(centerX, centerY + 80, `Cooldown: ${this.adManager.getCooldownString()}`, { fontSize: '13px', color: '#ff6666', fontFamily: 'Arial' }).setOrigin(0.5);
    }
    // Close button
    const closeBtn = this.add.text(centerX, centerY + 110, 'CLOSE', { fontSize: '18px', color: '#fff', backgroundColor: '#888', padding: { x: 18, y: 8 }, fontFamily: 'Arial' }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    closeBtn.on('pointerdown', () => { overlay.destroy(); panel.destroy(); closeBtn.destroy(); });
    closeBtn.on('pointerover', () => closeBtn.setStyle({ backgroundColor: '#555' }));
    closeBtn.on('pointerout', () => closeBtn.setStyle({ backgroundColor: '#888' }));
  }

  private createStarfield(): void {
    const stars = this.add.group();
    
    // Create different star layers for parallax effect
    for (let layer = 0; layer < 3; layer++) {
      const starCount = 50 - (layer * 15);
      const starSize = (layer + 1) * 0.5;
      const starAlpha = 1 - (layer * 0.3);
      
      for (let i = 0; i < starCount; i++) {
        const x = Phaser.Math.Between(0, this.cameras.main.width);
        const y = Phaser.Math.Between(0, this.cameras.main.height);
        
        const star = this.add.circle(x, y, starSize, 0xffffff, starAlpha);
        star.setDepth(layer);
        stars.add(star);
        
        // Twinkling effect
        this.tweens.add({
          targets: star,
          alpha: starAlpha * 0.3,
          duration: Phaser.Math.Between(1000, 3000),
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
          delay: Phaser.Math.Between(0, 2000)
        });
        
        // Slow movement for parallax
        this.tweens.add({
          targets: star,
          x: x + Phaser.Math.Between(-20, 20),
          y: y + Phaser.Math.Between(-20, 20),
          duration: Phaser.Math.Between(10000, 20000),
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut'
        });
      }
    }
  }

  private createFloatingParticles(): void {
    // Create energy particles floating across screen
    for (let i = 0; i < 15; i++) {
      this.time.delayedCall(i * 200, () => {
        this.createEnergyParticle();
      });
    }
    
    // Continuously spawn new particles
    this.time.addEvent({
      delay: 3000,
      callback: () => this.createEnergyParticle(),
      loop: true
    });
  }

  private createEnergyParticle(): void {
    const startX = -50;
    const startY = Phaser.Math.Between(100, this.cameras.main.height - 100);
    const endX = this.cameras.main.width + 50;
    const endY = startY + Phaser.Math.Between(-100, 100);
    
    // Create particle with glow
    const particle = this.add.circle(startX, startY, 3, 0x74b9ff, 0.8);
    particle.setDepth(10);
    
    // Glow effect
    const glow = this.add.circle(startX, startY, 8, 0x74b9ff, 0.3);
    glow.setDepth(9);
    
    // Movement animation
    this.tweens.add({
      targets: [particle, glow],
      x: endX,
      y: endY,
      duration: Phaser.Math.Between(8000, 12000),
      ease: 'Sine.easeInOut',
      onComplete: () => {
        particle.destroy();
        glow.destroy();
      }
    });
    
    // Pulsing effect
    this.tweens.add({
      targets: [particle, glow],
      scaleX: 1.5,
      scaleY: 1.5,
      alpha: 0.4,
      duration: 1000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  private createBackgroundEarth(): void {
    const earthX = this.cameras.main.width - 200;
    const earthY = this.cameras.main.height - 150;
    
    // Earth silhouette
    const earth = this.add.circle(earthX, earthY, 80, 0x4a90e2, 0.6);
    earth.setDepth(5);
    
    // Earth glow
    const earthGlow = this.add.circle(earthX, earthY, 100, 0x74b9ff, 0.2);
    earthGlow.setDepth(4);
    
    // Slow rotation
    this.tweens.add({
      targets: earth,
      rotation: Math.PI * 2,
      duration: 30000,
      repeat: -1,
      ease: 'Linear'
    });
    
    // Breathing glow effect
    this.tweens.add({
      targets: earthGlow,
      scaleX: 1.2,
      scaleY: 1.2,
      alpha: 0.1,
      duration: 3000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
    
    // Add atmospheric effect
    const atmosphere = this.add.circle(earthX, earthY, 85, 0x87ceeb, 0.1);
    atmosphere.setDepth(6);
    
    this.tweens.add({
      targets: atmosphere,
      alpha: 0.3,
      scaleX: 1.1,
      scaleY: 1.1,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }
} 